import { useEffect, useRef, useState } from 'react';

import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type LastGeneratedSource = () => Promise<string | null>;

export async function fetchLastGeneratedAt(): Promise<string | null> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/program-evaluation`, {
    headers: authHeaders(token),
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`program-evaluation fetch failed: ${response.status}`);
  }
  const data = (await response.json()) as { generatedAt?: string };
  return data.generatedAt ?? null;
}

export function formatLastGenerated(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function useLastGenerated(source: LastGeneratedSource = fetchLastGeneratedAt): {
  lastGeneratedAt: string | null;
  loading: boolean;
} {
  const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const sourceRef = useRef(source);

  useEffect(() => {
    let active = true;
    sourceRef
      .current()
      .then((value) => {
        if (active) setLastGeneratedAt(value);
      })
      .catch(() => {
        if (active) setLastGeneratedAt(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { lastGeneratedAt, loading };
}
